import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Play, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { semanticsApi, type SemanticEntityView } from '../../services/semantics-api';
import { handleApiError } from '../../services/api';
import { PreviewResult } from './DefinitionView';

/**
 * Power-user escape hatch: edit a definition's raw JSON directly. A human's
 * manual edit goes LIVE immediately (update + confirm) — the human is
 * authoritative. Validation/compilation happen server-side on save and on the
 * live Preview.
 */
const EditView: React.FC<{ entity: SemanticEntityView; onDone: () => void }> = ({ entity, onDone }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const initial = useMemo(
    () => JSON.stringify(entity.proposedDefinition ?? entity.confirmedDefinition ?? {}, null, 2),
    [entity.proposedDefinition, entity.confirmedDefinition],
  );
  const [text, setText] = useState(initial);
  const [note, setNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const parsed = useMemo((): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } => {
    try {
      const value = JSON.parse(text);
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { ok: false, error: 'Definition must be a JSON object' };
      }
      return { ok: true, value };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
    }
  }, [text]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['semanticEntity', entity.id] });
    void queryClient.invalidateQueries({ queryKey: ['semanticVersions', entity.id] });
    void queryClient.invalidateQueries({ queryKey: ['semanticsOverview'] });
  };

  const preview = useMutation({
    mutationFn: () => semanticsApi.preview(entity.id, (parsed as { value: Record<string, unknown> }).value),
  });

  const save = useMutation({
    mutationFn: () =>
      semanticsApi.saveVersion(entity.id, {
        definition: (parsed as { value: Record<string, unknown> }).value,
        ...(note.trim() ? { change_note: note.trim() } : {}),
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Saved — live from the next query');
      onDone();
    },
    onError: (e) => toast.error(handleApiError(e) || 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: () => semanticsApi.remove(entity.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['semanticsOverview'] });
      toast.success('Definition deleted');
      navigate('/semantics', { replace: true });
    },
    onError: (e) => toast.error(handleApiError(e) || 'Delete failed'),
  });

  const canPreview = parsed.ok && entity.type !== 'glossary';

  return (
    <div className="px-5 py-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Definition JSON
        </div>
        <div className="text-[11px] text-muted-foreground">
          A manual edit goes live immediately.
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        rows={16}
        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 font-mono text-[12px] text-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-y"
      />

      {!parsed.ok && <p className="text-[11.5px] text-destructive">⚠ {parsed.error}</p>}

      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Change note (optional)"
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!parsed.ok || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-lg bg-primary px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {canPreview && (
          <button
            type="button"
            disabled={preview.isPending}
            onClick={() => preview.mutate()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-input px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted disabled:opacity-50"
          >
            <Play className="w-3 h-3" /> {preview.isPending ? 'Running…' : 'Preview'}
          </button>
        )}
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>

        <div className="ml-auto">
          {confirmDelete ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[11.5px] text-muted-foreground">Delete this definition?</span>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
                className="rounded-lg bg-destructive px-2.5 py-1 text-[11.5px] font-semibold text-destructive-foreground disabled:opacity-50"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-[11.5px] text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>

      {preview.isError && <p className="text-[11.5px] text-destructive">{handleApiError(preview.error)}</p>}
      {preview.data && <PreviewResult result={preview.data} />}
    </div>
  );
};

export default EditView;
